import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { addDays } from '../../../components/novarium/periodRange'
import { HospitalizationDailyPointDTO } from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { useChartTokens } from '../../ServicesStatistics/chartTokens'
import { PeakAnalysis } from '../../ServicesStatistics/peaks'
import { escapeHtml, parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { axisLabel } from '../../ServicesStatistics/TimelineChart'
import {
  dayTitleOf,
  HospitalizationMeasure,
  isLowVolume,
  pointValue,
} from '../hospitalizationEvolutionModel'
import { formatCount, formatPercent } from '../hospitalizationSummaryFormat'

interface HospitalizationEvolutionChartProps {
  days: HospitalizationDailyPointDTO[]
  measure: HospitalizationMeasure
  /** Nombre accesible del gráfico: la medida y el período («Ocupación diaria»). */
  ariaLabel: string
  /** Promedio del período para la línea de referencia (ocupación del período o ingresos / egresos por día); null: sin línea. */
  average: number | null
  /** Picos de la ocupación, uno por posición de `days`; null con «Ingresos» y «Egresos» (no hay picos). */
  analysis: PeakAnalysis | null
  /** Click, tap o Enter sobre un día: abre su panel. `opener` es a donde vuelve el foco al cerrarlo. */
  onOpen: (day: HospitalizationDailyPointDTO, opener: HTMLElement | null) => void
}

const CHART_HEIGHT = 280
const HALF_DAY_MS = 12 * 60 * 60 * 1000
/** Marcador de un punto común, de un pico y del punto resaltado con el teclado. */
const MARKER_SIZE = 3
const PEAK_MARKER_SIZE = 7
const ACTIVE_MARKER_SIZE = 8
/** ApexCharts avisa del click por dos eventos (`markerClick` y `dataPointSelection`): el segundo, dentro de este tiempo, se ignora. */
const DUPLICATE_CLICK_MS = 400
/** El eje de la ocupación llega a 100 %, y pasa de ahí de a 20 puntos si algún día lo supera (sobreocupación). */
const OCCUPANCY_AXIS_STEP = 20
const OCCUPANCY_AXIS_BASE = 100

/** Un punto del eje de tiempo: con `day` es un día dibujado; sin él, un extremo del rango que no tiene valor. */
interface PlotPoint {
  x: number
  y: number | null
  day: HospitalizationDailyPointDTO | null
  /** Posición del día en `days` (y en `analysis`). */
  index: number
}

const dayStart = (day: HospitalizationDailyPointDTO): Date => parseIsoDate(day.date)

const SERIES_NAME_ID: Record<HospitalizationMeasure, string> = {
  occupancy: 'HospitalizationSummaryKpiOccupancy',
  admissions: 'HospitalizationSummaryKpiAdmissions',
  discharges: 'HospitalizationSummaryKpiDischarges',
}

interface PointFlags {
  peak: boolean
  lowVolume: boolean
}

/** El valor de la medida escrito: «72,3 %» o «14». */
function valueText(intl: IntlShape, day: HospitalizationDailyPointDTO, measure: HospitalizationMeasure): string {
  const value = pointValue(day, measure)
  if (value === null) return '—'
  return measure === 'occupancy' ? formatPercent(intl, value) : formatCount(intl, value)
}

/** Tooltip del día: su título, el valor de la medida (con las camas si es la ocupación) y las notas del día. */
function tooltipHtml(intl: IntlShape, day: HospitalizationDailyPointDTO, measure: HospitalizationMeasure, flags: PointFlags): string {
  const text = (id: string, values?: Record<string, string | number>) => escapeHtml(intl.formatMessage({ id }, values))
  const lines = [`<div class="nv-hosp-tip__title">${escapeHtml(dayTitleOf(intl, day.date))}</div>`]
  lines.push(`<div>${text(SERIES_NAME_ID[measure])}: ${escapeHtml(valueText(intl, day, measure))}</div>`)
  if (measure === 'occupancy') {
    lines.push(`<div>${text('HospitalizationSummaryOfBeds', {
      count: formatCount(intl, day.patientDays), total: formatCount(intl, day.bedsAvailable),
    })}</div>`)
  }
  if (flags.peak) lines.push(`<div class="nv-hosp-tip__note nv-hosp-tip__note--peak">${text('HospitalizationSummaryTipPeak')}</div>`)
  if (!day.complete) lines.push(`<div class="nv-hosp-tip__note">${text('HospitalizationSummaryTipIncomplete')}</div>`)
  else if (flags.lowVolume) lines.push(`<div class="nv-hosp-tip__note">${text('HospitalizationSummaryTipLow')}</div>`)
  lines.push(`<div class="nv-hosp-tip__note">${text('HospitalizationSummaryTipOpen')}</div>`)
  return `<div class="nv-hosp-tip">${lines.join('')}</div>`
}

/**
 * Evolución diaria: una línea con un punto por día, de la ocupación, los ingresos o los egresos. El eje X es un eje de
 * tiempo real: ApexCharts ralea solo los rótulos para que no se pisen. Una línea punteada marca el promedio del período
 * (la ocupación del indicador, que es ponderada, o los ingresos / egresos por día). Con la ocupación, los días de pocas
 * camas llevan un marcador hueco (el % puede no ser representativo) y los picos, un marcador más grande con su etiqueta. Un
 * día sin valor (ocupación sin camas) no se dibuja, no se alcanza con el teclado y no abre panel: no cuenta como 0 %. Un
 * click, un tap o Enter sobre un día abre su panel; con el teclado, el contenedor es un grupo enfocable: flechas e
 * Inicio/Fin mueven un punto resaltado y una región `aria-live` dice dónde está (el mismo patrón que la evolución de
 * Atenciones de urgencia y Recién nacidos). El aviso del teclado es la descripción accesible del grupo.
 * TODO(design-system): gráficos. Novarium no define el estilo de los gráficos.
 * TODO(design-system): color de pico en gráficos. Se usa el par --warn / --warn-bg de la etiqueta «Atención» del ranking.
 */
export default function HospitalizationEvolutionChart({
  days, measure, ariaLabel, average, analysis, onOpen,
}: HospitalizationEvolutionChartProps) {
  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  const hintId = useId()
  const tokens = useChartTokens(ref)
  const isOccupancy = measure === 'occupancy'
  /** Posición en `plot` del día resaltado con el teclado (apuntarlo con el mouse lo resuelve el tooltip). */
  const [activePlot, setActivePlot] = useState<number | null>(null)
  const lastOpenRef = useRef(0)

  /**
   * Lo que se dibuja: los días con valor. Se agregan puntos sin valor en los extremos del rango, para que el eje de tiempo
   * abarque todo el rango y ApexCharts no lo achique a los puntos dibujados (con un solo punto no sabe dibujar los rótulos
   * del eje); un rango de un solo día lleva un día más a cada lado.
   */
  const plot = useMemo<PlotPoint[]>(() => {
    if (days.length === 0) return []
    const drawn: PlotPoint[] = days.flatMap((day, index) => {
      const y = pointValue(day, measure)
      return y !== null ? [{ x: dayStart(day).getTime(), y, day, index }] : []
    })
    const spare = days.length === 1 ? 1 : 0
    const lowX = addDays(dayStart(days[0]), -spare).getTime()
    const highX = addDays(dayStart(days[days.length - 1]), spare).getTime()
    if (drawn.length === 0 || drawn[0].x > lowX) drawn.unshift({ x: lowX, y: null, day: null, index: -1 })
    if (drawn[drawn.length - 1].x < highX) drawn.push({ x: highX, y: null, day: null, index: -1 })
    return drawn
  }, [days, measure])

  const series = useMemo(() => [{
    name: intl.formatMessage({ id: SERIES_NAME_ID[measure] }),
    data: plot.map(({ x, y }) => ({ x, y })),
  }], [intl, plot, measure])

  const flagsOf = useCallback((index: number): PointFlags => ({
    peak: analysis?.peaks.includes(index) ?? false,
    lowVolume: isLowVolume(days[index], measure),
  }), [analysis, days, measure])

  /** Los días a los que se llega con las flechas: los dibujados. */
  const navigable = useMemo(() => plot.flatMap((item, position) => (item.day ? [position] : [])), [plot])

  useEffect(() => { setActivePlot(null) }, [plot])

  /** Abre el panel del día en la posición `position` de `plot`. */
  const openAt = useCallback((position: number) => {
    const day = plot[position]?.day
    if (!day) return
    const now = Date.now()
    if (now - lastOpenRef.current < DUPLICATE_CLICK_MS) return
    lastOpenRef.current = now
    setActivePlot(position)
    onOpen(day, ref.current)
  }, [plot, onOpen])

  const options = useMemo<ApexOptions | null>(() => {
    if (!tokens || plot.length === 0 || !ref.current) return null
    const rangeStart = dayStart(days[0]).getTime()
    const rangeEnd = dayStart(days[days.length - 1]).getTime()
    const warnBg = getComputedStyle(ref.current).getPropertyValue('--warn-bg').trim()
    const font = { fontFamily: tokens.font, fontSize: '12px' }
    const maxValue = plot.reduce((max, item) => Math.max(max, item.y ?? 0), 0)
    const occupancyMax = Math.max(OCCUPANCY_AXIS_BASE, Math.ceil(maxValue / OCCUPANCY_AXIS_STEP) * OCCUPANCY_AXIS_STEP)

    // Marcadores huecos: días de pocas camas (solo la ocupación)
    const hollow = plot.flatMap((item, position) => (item.day && isLowVolume(item.day, measure)
      ? [{
        seriesIndex: 0, dataPointIndex: position, fillColor: tokens.card, strokeColor: tokens.primary,
        size: MARKER_SIZE, shape: 'circle' as const,
      }] : []))

    const peakAnnotations = plot.flatMap((item) => {
      if (!item.day || item.y === null || !analysis?.peaks.includes(item.index)) return []
      return [{
        x: item.x,
        y: item.y,
        marker: { size: PEAK_MARKER_SIZE, fillColor: tokens.warn, strokeColor: warnBg, strokeWidth: 2 },
        label: {
          text: `${axisLabel(intl, item.x, 'DAY')} · ${formatPercent(intl, item.y)}`,
          borderColor: tokens.warn,
          borderWidth: 1,
          offsetY: -4,
          style: { ...font, color: tokens.warn, background: warnBg, fontWeight: 600 },
        },
      }]
    })

    const activeItem = activePlot === null ? null : plot[activePlot]
    const activeAnnotations = activeItem && activeItem.day && activeItem.y !== null ? [{
      x: activeItem.x,
      y: activeItem.y,
      marker: { size: ACTIVE_MARKER_SIZE, fillColor: tokens.card, strokeColor: tokens.textStrong, strokeWidth: 2 },
    }] : []

    const averageText = average === null ? '' : isOccupancy
      ? intl.formatMessage({ id: 'HospitalizationSummaryAverageOccupancy' }, { value: formatPercent(intl, average) })
      : intl.formatMessage({ id: 'HospitalizationSummaryAveragePerDay' }, { value: intl.formatNumber(average, { maximumFractionDigits: 1, minimumFractionDigits: 1 }) })

    return {
      chart: {
        type: 'line',
        height: CHART_HEIGHT,
        fontFamily: tokens.font,
        foreColor: tokens.text2,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        events: {
          // Con el tooltip compartido ApexCharts avisa del click con el día más cercano, no hace falta acertarle al punto
          markerClick: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
          dataPointSelection: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
        },
      },
      colors: [tokens.primary],
      states: { active: { filter: { type: 'none' } } },
      stroke: { width: 2, curve: 'straight', lineCap: 'round' },
      markers: {
        size: MARKER_SIZE,
        strokeWidth: 2,
        strokeColors: [tokens.primary],
        discrete: hollow,
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      grid: { borderColor: tokens.divider, strokeDashArray: 0 },
      annotations: {
        yaxis: average !== null ? [{
          y: average,
          borderColor: tokens.text2,
          strokeDashArray: 4,
          label: {
            text: averageText,
            position: 'left',
            textAnchor: 'start',
            offsetX: 4,
            borderWidth: 0,
            style: { ...font, color: tokens.text2, background: 'transparent' },
          },
        }] : [],
        points: [...peakAnnotations, ...activeAnnotations],
      },
      xaxis: {
        type: 'datetime',
        axisBorder: { color: tokens.divider },
        axisTicks: { color: tokens.divider },
        tooltip: { enabled: false },
        labels: {
          rotate: 0,
          datetimeUTC: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          style: { fontSize: '12px' },
          // Con un solo punto ApexCharts no pasa la marca de tiempo: el valor es la marca misma
          formatter: (value: string, timestamp?: number) => {
            const time = timestamp ?? Number(value)
            // Los días vacíos de los extremos (ver `plot`) no llevan rótulo: no son parte del rango
            if (!Number.isFinite(time) || time < rangeStart - HALF_DAY_MS || time > rangeEnd + HALF_DAY_MS) return ''
            return axisLabel(intl, time, 'DAY')
          },
        },
      },
      yaxis: {
        min: 0,
        ...(isOccupancy
          ? { max: occupancyMax, tickAmount: occupancyMax / OCCUPANCY_AXIS_STEP }
          : {
            forceNiceScale: true,
            title: {
              text: intl.formatMessage({ id: 'HospitalizationSummaryAxisHospitalizations' }),
              style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
            },
          }),
        labels: {
          style: { fontSize: '12px' },
          formatter: (value: number) => (isOccupancy ? `${formatCount(intl, Math.round(value))} %` : formatCount(intl, Math.round(value))),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          // Un punto sin valor es un extremo del rango: se muestra el día dibujado más cercano
          const item = plot[dataPointIndex]?.day
            ? plot[dataPointIndex]
            : (dataPointIndex <= 0 ? plot.find((candidate) => candidate.day) : [...plot].reverse().find((candidate) => candidate.day))
          return item?.day ? tooltipHtml(intl, item.day, measure, flagsOf(item.index)) : ''
        },
      },
    }
  }, [intl, tokens, plot, days, isOccupancy, measure, average, analysis, activePlot, openAt, flagsOf])

  // ── Teclado ──

  /** Lo que se dice (y se ve) de un día: «Sábado 19/09/2026: ocupación 72,3 %, 142 de 196 camas. Pico.» */
  const describe = (position: number): string => {
    const item = plot[position]
    if (!item?.day) return ''
    const day = item.day
    const period = dayTitleOf(intl, day.date)
    const flags = flagsOf(item.index)
    const parts = [
      isOccupancy
        ? intl.formatMessage({ id: 'HospitalizationSummaryLiveOccupancy' }, {
          period,
          value: valueText(intl, day, measure),
          count: formatCount(intl, day.patientDays),
          total: formatCount(intl, day.bedsAvailable),
        })
        : intl.formatMessage({ id: measure === 'admissions' ? 'HospitalizationSummaryLiveAdmissions' : 'HospitalizationSummaryLiveDischarges' }, {
          period,
          count: pointValue(day, measure) ?? 0,
          countText: valueText(intl, day, measure),
        }),
      flags.peak ? intl.formatMessage({ id: 'HospitalizationSummaryLivePeak' }) : '',
      !day.complete ? intl.formatMessage({ id: 'HospitalizationSummaryLiveIncomplete' }) : flags.lowVolume ? intl.formatMessage({ id: 'HospitalizationSummaryLiveLow' }) : '',
    ]
    return parts.filter(Boolean).join(' ')
  }

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    if (navigable.length === 0) return
    const current = activePlot === null ? -1 : navigable.indexOf(activePlot)
    let next: number | null = null
    if (event.key === 'ArrowRight') next = Math.min(navigable.length - 1, current + 1)
    else if (event.key === 'ArrowLeft') next = current < 0 ? navigable.length - 1 : Math.max(0, current - 1)
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = navigable.length - 1
    else if ((event.key === 'Enter' || event.key === ' ') && activePlot !== null) {
      event.preventDefault()
      openAt(activePlot)
      return
    }
    if (next !== null) {
      event.preventDefault()
      setActivePlot(navigable[next])
    }
  }

  const activeText = activePlot === null ? '' : describe(activePlot)

  return (
    <div className="nv-hosp-timeline">
      <div
        ref={ref}
        className="nv-hosp-chart"
        role="group"
        aria-label={ariaLabel}
        aria-roledescription={intl.formatMessage({ id: 'HospitalizationSummaryChartRole' })}
        aria-describedby={hintId}
        tabIndex={0}
        onKeyDown={move}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActivePlot(null) }}
      >
        <div aria-hidden="true">
          {options && (
            <ReactApexChart key={measure} type="line" height={CHART_HEIGHT} series={series} options={options} />
          )}
        </div>
        <span id={hintId} className="nv-sr-only">{intl.formatMessage({ id: 'HospitalizationSummaryChartHint' })}</span>
        <span className="nv-sr-only" role="status" aria-live="polite">{activeText}</span>
      </div>
      {/* Solo el día resaltado con el teclado; el lector de pantalla lo oye por la región `aria-live` de arriba */}
      {activeText && <p className="nv-hosp-timeline__detail nv-help" aria-hidden="true">{activeText}</p>}
    </div>
  )
}
